/**
 * Get the tenant's base domain (with port) for building URLs.
 * Uses the tenant's activeDomain from server context, falling back to window.location.
 */
function getTenantBase(): { protocol: string; domain: string } {
    if (process.server) {
        const event = useRequestEvent()
        const tenantDomain = event?.context?.tenantDomain
        const host = event?.headers?.get('host') || event?.headers?.get('x-forwarded-host') || 'localhost:3000'
        const port = host.includes(':') ? `:${host.split(':')[1]}` : ''
        // Trust the reverse-proxy header. Without this every SSR-built
        // cross-subdomain link is `http://shop.{tenant}/...` and the
        // browser logs a mixed-content warning (or, under strict CSP,
        // blocks the navigation). Caddy already strips inbound
        // `X-Forwarded-Proto` from clients and sets its own value
        // before forwarding upstream, so this header is trustworthy
        // here. Fall back to https in production, http in dev.
        const fwdProto = event?.headers?.get('x-forwarded-proto')
        const protocol = fwdProto
            ? `${fwdProto.split(',')[0].trim()}:`
            : (process.env.NODE_ENV === 'production' ? 'https:' : 'http:')
        return { protocol, domain: tenantDomain ? `${tenantDomain}${port}` : host }
    }

    // Client-side: read from tenant state (populated by plugin), with window.location fallback
    const tenant = useState<any>('tenant')
    const activeDomain = tenant.value?.activeDomain
    if (activeDomain) {
        const port = window.location.port ? `:${window.location.port}` : ''
        return { protocol: window.location.protocol, domain: `${activeDomain}${port}` }
    }

    return { protocol: window.location.protocol, domain: window.location.host }
}

export const getSubdomainUrl = (subdomain: string, path = '/') => {
    const { protocol, domain } = getTenantBase()
    return `${protocol}//${subdomain}.${domain}${path}`
}

export const getMainDomainUrl = (path = '/') => {
    const { protocol, domain } = getTenantBase()
    return `${protocol}//${domain}${path}`
}
